import React, { useState, useEffect } from 'react';
import { 
  BarChart3, DollarSign, Zap, TrendingUp, Calendar,
  Activity, AlertTriangle
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Card } from '../../ui/card';
import { useToast } from '../../../hooks/use-toast';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const UsageView = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(7);
  const { toast } = useToast();
  const API_BASE_URL = '/api';

  const timeRanges = [
    { value: 1, label: 'Today' },
    { value: 7, label: '7 Days' },
    { value: 30, label: '30 Days' }
  ];

  useEffect(() => {
    fetchStats();
  }, [timeRange]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/ai-controls/usage/stats?days=${timeRange}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch usage stats');

      const data = await response.json();
      setStats(data.data);
    } catch (error) {
      toast({
        title: '❌ Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatFeatureName = (name) => {
    return name.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
            Usage & Cost Dashboard
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Track tokens, costs, latency, and errors by feature
          </p>
        </div>

        {/* Time Range Filter */}
        <div className="flex gap-2">
          {timeRanges.map(range => (
            <button
              key={range.value}
              onClick={() => setTimeRange(range.value)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                timeRange === range.value
                  ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:text-gray-900 border border-gray-200'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : !stats ? (
        <Card className="p-12 text-center">
          <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No usage data available</p>
        </Card>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="p-6 bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200">
              <div className="flex items-center justify-between mb-2">
                <Activity className="w-8 h-8 text-blue-600" />
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900">
                    {stats.total_requests.toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-600">Total Requests</div>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200">
              <div className="flex items-center justify-between mb-2">
                <Zap className="w-8 h-8 text-purple-600" />
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900">
                    {stats.total_tokens.toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-600">Total Tokens</div>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="w-8 h-8 text-green-600" />
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900">
                    ${stats.total_cost.toFixed(4)}
                  </div>
                  <div className="text-xs text-gray-600">Total Cost</div>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="w-8 h-8 text-amber-600" />
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900">
                    {stats.avg_latency_ms}ms
                  </div>
                  <div className="text-xs text-gray-600">Avg Latency</div>
                </div>
              </div>
            </Card>
          </div>

          {/* Success & Error Rates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Performance</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Success Rate</span>
                    <span className="font-semibold text-green-600">{stats.success_rate}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className="bg-gradient-to-r from-green-500 to-emerald-500 h-3 rounded-full transition-all"
                      style={{ width: `${stats.success_rate}%` }}
                    />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Error Rate</span>
                    <span className="font-semibold text-red-600">{stats.error_rate}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className="bg-gradient-to-r from-red-500 to-rose-500 h-3 rounded-full transition-all"
                      style={{ width: `${stats.error_rate}%` }}
                    />
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Top Errors</h3>
              {stats.top_errors && stats.top_errors.length > 0 ? (
                <div className="space-y-3">
                  {stats.top_errors.slice(0, 3).map((error, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-600" />
                        <span className="text-sm font-medium text-gray-900">{error.type}</span>
                      </div>
                      <span className="text-sm font-semibold text-red-600">{error.count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-sm text-gray-500 py-8">
                  No errors recorded 🎉
                </div>
              )}
            </Card>
          </div>

          {/* Usage by Feature */}
          <Card className="p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Usage by Feature</h3>
            <div className="space-y-4">
              {Object.entries(stats.by_feature || {}).map(([feature, data]) => (
                <div key={feature} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900">{formatFeatureName(feature)}</h4>
                    <div className="flex gap-6 text-sm">
                      <div>
                        <span className="text-gray-600">Requests: </span>
                        <span className="font-semibold">{data.requests.toLocaleString()}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Tokens: </span>
                        <span className="font-semibold">{data.tokens.toLocaleString()}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Cost: </span>
                        <span className="font-semibold">${data.cost.toFixed(4)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full transition-all"
                      style={{ width: `${(data.requests / stats.total_requests) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Daily Trend Chart */}
          {stats.by_day && stats.by_day.length > 0 && (
            <Card className="p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Daily Trends</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={stats.by_day}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="requests" 
                      stroke="#10b981" 
                      strokeWidth={2}
                      name="Requests"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="errors" 
                      stroke="#ef4444" 
                      strokeWidth={2}
                      name="Errors"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default UsageView;